import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "./ThemeContext";

const AuthContext = createContext(null);

var BACKEND = process.env.REACT_APP_BACKEND_URL || "https://gaurilabs-production.up.railway.app";

// Helper — backend returns user directly OR wrapped in { user: {...} }
function extractUser(data) {
  if (!data) return null;
  // If response has a nested user object, use it; otherwise use data directly
  return data.user || data;
}

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const { setThemeId }      = useTheme();

  const refreshMe = useCallback(async function () {
    try {
      const { data } = await api.get("/auth/me");
      const userData = extractUser(data);
      setUser(userData);
      if (userData && userData.theme) setThemeId(userData.theme);
      return userData;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, [setThemeId]);

  useEffect(function () { refreshMe(); }, [refreshMe]);

  // ── Auto-logout on browser/tab close ──────────────────────
  useEffect(function () {
    function handleUnload() {
      try {
        navigator.sendBeacon(BACKEND + "/api/auth/logout");
      } catch (e) {
        fetch(BACKEND + "/api/auth/logout", {
          method: "POST", keepalive: true, credentials: "include",
        }).catch(function () {});
      }
      setUser(false);
    }
    window.addEventListener("beforeunload", handleUnload);
    return function () { window.removeEventListener("beforeunload", handleUnload); };
  }, []);

  const login = useCallback(async function (email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const userData = extractUser(data);
      setUser(userData);
      if (userData && userData.theme) setThemeId(userData.theme);
      return { ok: true, user: userData };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(
          e.response && e.response.data && e.response.data.detail
        ) || e.message
      };
    }
  }, [setThemeId]);

  const register = useCallback(async function (payload) {
    try {
      const { data } = await api.post("/auth/register", payload);
      const userData = extractUser(data);
      setUser(userData);
      if (userData && userData.theme) setThemeId(userData.theme);
      return { ok: true, user: userData };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(
          e.response && e.response.data && e.response.data.detail
        ) || e.message
      };
    }
  }, [setThemeId]);

  const logout = useCallback(async function () {
    try { await api.post("/auth/logout"); } catch (e) {}
    setUser(false);
  }, []);

  const updateTheme = useCallback(async function (theme) {
    try {
      const { data } = await api.post("/auth/theme", { theme });
      const userData = extractUser(data);
      if (userData) setUser(userData);
    } catch (e) {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshMe, updateTheme }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
